import { TypographyH2, TypographyMuted, TypographyP } from '@talentmesh/ui/components/typography';
import SectionLoadingState from '../../SectionLoadingState';
import { useAtsConnectionStatus, useAtsIntegrationTools } from '../api/useAtsIntegration';
import AtsIntegrationStatus from './AtsIntegrationStatus';
import ConnectAtsCard from './ConnectAtsCard';
import { useUserContext } from '@/Context/UserContext';
import useCompanyId from '@/Hooks/UseCompanyId';
import UIStrings from '@/Utils/UIStrings';

const AtsIntegrationSection = () => {
    const {
        data: connection,
        isLoading: connectionLoading,
        error: connectionError,
        mutate,
    } = useAtsConnectionStatus();
    const { data: tools, isLoading: toolsLoading, error: toolsError } = useAtsIntegrationTools();
    const companyId = useCompanyId();
    const { memberships } = useUserContext();

    // Only company admins/owners may connect or disconnect the org-wide ATS integration; any member
    // may see which ATS the company is connected to.
    const membership = memberships.find((m) => m.companyId === companyId);
    const canManage = membership?.role === 'Owner' || membership?.role === 'Admin';

    // A company with no ATS connected is the normal starting state, not a failure: the status hook
    // turns the API's 404 into null, so only genuine errors reach `connectionError`.
    const connectedTool = tools?.find((tool) => tool.id.toLowerCase() === connection?.atsTool.toLowerCase());

    const renderContent = () => {
        if (connection) {
            return (
                <AtsIntegrationStatus
                    connection={connection}
                    tool={connectedTool}
                    canManage={canManage}
                    onDisconnected={() => mutate()}
                />
            );
        }

        if (!canManage) {
            return <TypographyMuted>{UIStrings.OnlyAdminsCanManageAtsIntegration}</TypographyMuted>;
        }

        return (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {tools?.map((tool) => (
                    <ConnectAtsCard
                        key={tool.id}
                        tool={tool.id}
                        label={tool.label}
                        logoUrl={tool.assets.iconUrl ?? tool.assets.logoUrl}
                        connectionGuideUrl={tool.connectionGuideUrl}
                        onConnected={() => mutate()}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <TypographyH2>{UIStrings.AtsIntegration}</TypographyH2>
                <TypographyP>{UIStrings.ConnectYourAtsDescription}</TypographyP>
            </div>
            <SectionLoadingState
                isLoading={connectionLoading || toolsLoading}
                loadingFailed={!!(connectionError || toolsError)}
            >
                {renderContent()}
            </SectionLoadingState>
        </div>
    );
};

export default AtsIntegrationSection;
